import type { Environment, MfeVersion } from "./types";
import { buildSnapshotOverrideToken, filterIgnoredMfes } from "./snapshot-utils";

const PREVIEW_ENVIRONMENT_ORIGINS: Record<Environment, string> = {
  dev: "https://platform.dev.accuris.dev",
  stage: "https://platform.stage.accuris.dev",
  prod: "https://platform.accuris.dev",
};

export const DEFAULT_PREVIEW_ROUTE = "/thread";

const PRODUCT_PREVIEW_ROUTE_OVERRIDES: Record<string, string> = {
  udvp: "/view/industrial/document/LWTLDAAAAAAAAAAA",
};

export const PREVIEW_HISTORY_KEY = "hawaii-dashboard-preview-history";
const MAX_PREVIEW_HISTORY = 10;

export interface PreviewConfig {
  environment: Environment;
  route: string;
  overrides: Map<string, string>;
}

export interface PreviewHistoryEntry {
  url: string;
  environment: Environment;
  overrideCount: number;
  timestamp: string;
}

export function getPreviewOrigin(environment: Environment): string {
  return PREVIEW_ENVIRONMENT_ORIGINS[environment];
}

export function getDefaultPreviewRoute(productName?: string | null, fallbackRoute: string = DEFAULT_PREVIEW_ROUTE): string {
  if (productName && PRODUCT_PREVIEW_ROUTE_OVERRIDES[productName]) {
    return PRODUCT_PREVIEW_ROUTE_OVERRIDES[productName];
  }

  return fallbackRoute;
}

export function buildPreviewUrl(config: PreviewConfig): string {
  const origin = getPreviewOrigin(config.environment);
  const route = config.route.startsWith("/") ? config.route : `/${config.route}`;
  const url = new URL(`${origin}${route}`);

  if (config.overrides.size > 0) {
    const tokens = Array.from(config.overrides.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, version]) =>
        buildSnapshotOverrideToken(name, { major: "", resolved: version }),
      );
    url.searchParams.set("overrides", tokens.join(","));
  }

  return url.toString();
}

export function parsePreviewUrl(url: string): PreviewConfig | null {
  try {
    const parsed = new URL(url);

    let environment: Environment | null = null;
    for (const [env, origin] of Object.entries(PREVIEW_ENVIRONMENT_ORIGINS)) {
      if (parsed.origin === origin) {
        environment = env as Environment;
        break;
      }
    }
    if (!environment) return null;

    const route = parsed.pathname || "/";

    const overrides = new Map<string, string>();
    const raw = parsed.searchParams.get("overrides");
    if (raw) {
      for (const token of raw.split(",")) {
        const lastUnderscore = token.lastIndexOf("_");
        if (lastUnderscore > 0) {
          const name = token.substring(0, lastUnderscore);
          const version = token.substring(lastUnderscore + 1);
          if (name && version) {
            overrides.set(name, version);
          }
        }
      }
    }

    return { environment, route, overrides };
  } catch {
    return null;
  }
}

export function buildOverridesFromVersions(
  versions: Record<string, MfeVersion>,
  currentMfes: Record<string, MfeVersion>,
): Map<string, string> {
  const filtered = filterIgnoredMfes(versions);
  const overrides = new Map<string, string>();

  for (const [name, version] of Object.entries(filtered)) {
    const current = currentMfes[name];
    if (current && current.resolved !== version.resolved) {
      overrides.set(name, version.resolved);
    }
  }

  return overrides;
}

export function encodeDashboardPreviewUrl(config: PreviewConfig): string {
  const params = new URLSearchParams();
  params.set("env", config.environment);
  params.set("route", config.route);

  if (config.overrides.size > 0) {
    const tokens = Array.from(config.overrides.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, version]) => `${name}_${version}`);
    params.set("overrides", tokens.join(","));
  }

  return `/preview?${params.toString()}`;
}

export function decodeDashboardPreviewParams(searchParams: URLSearchParams): Partial<PreviewConfig> {
  const result: Partial<PreviewConfig> = {};

  const env = searchParams.get("env");
  if (env === "dev" || env === "stage" || env === "prod") {
    result.environment = env;
  }

  const route = searchParams.get("route");
  if (route) {
    result.route = route;
  }

  const raw = searchParams.get("overrides");
  if (raw) {
    const overrides = new Map<string, string>();
    for (const token of raw.split(",")) {
      const lastUnderscore = token.lastIndexOf("_");
      if (lastUnderscore > 0) {
        const name = token.substring(0, lastUnderscore);
        const version = token.substring(lastUnderscore + 1);
        if (name && version) {
          overrides.set(name, version);
        }
      }
    }
    if (overrides.size > 0) {
      result.overrides = overrides;
    }
  }

  return result;
}

export function loadPreviewHistory(): PreviewHistoryEntry[] {
  try {
    const raw = localStorage.getItem(PREVIEW_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addPreviewHistoryEntry(entry: PreviewHistoryEntry): void {
  const history = loadPreviewHistory();
  // Remove duplicate URLs
  const filtered = history.filter((h) => h.url !== entry.url);
  filtered.unshift(entry);
  localStorage.setItem(
    PREVIEW_HISTORY_KEY,
    JSON.stringify(filtered.slice(0, MAX_PREVIEW_HISTORY)),
  );
}
